import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DOCUMENT, inject, Renderer2, signal } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  ActivatedRoute,
  Router,
  RouterLink,
  RouterModule,
} from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApiService } from '@solcre-org/core';
import { AlertService, AlertType, LayoutAuth } from '@solcre-org/core-ui';
import { environment } from '../../../../environments/environment.local';

@Component({
  selector: 'app-recover-password',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    RouterModule,
    RouterLink,
    LayoutAuth,
  ],
  templateUrl: './recover-password.html',
})
export class RecoverPassword {
  private apiService = inject(ApiService);
  private formBuilder: FormBuilder = inject(FormBuilder);
  private renderer: Renderer2 = inject(Renderer2);
  private document: Document = inject(DOCUMENT);
  private alertService: AlertService = inject(AlertService);
  private router: Router = inject(Router);
  private translateService: TranslateService = inject(TranslateService);
  private route: ActivatedRoute = inject(ActivatedRoute);

  form!: FormGroup;
  loading = signal<boolean>(false);
  showNewPassword = signal<boolean>(false);
  showConfirmPassword = signal<boolean>(false);

  token: string = '';
  config: any = {
    mainLogoUrl: '/assets/images/logo_blanco.svg',
  };

  ngOnInit(): void {
    this.renderer.addClass(this.document.documentElement, 'page-styles');
    this.renderer.addClass(this.document.documentElement, 'is-onboarding');

    this.token = this.route.snapshot.queryParams['token'];
    if (!this.token) {
      this.translateService
        .get('recoverPassword.noToken')
        .subscribe((translatedMessage: string) => {
          this.alertService.show({
            type: AlertType.ERROR,
            message: translatedMessage,
            duration: 5000,
            inBottom: true,
          });

          setTimeout(() => {
            this.router.navigate([environment.STATES.LOGIN]);
          }, 5000);
        });
    }

    this.form = this.formBuilder.group(
      {
        newPassword: this.formBuilder.control('', [
          Validators.required,
          Validators.minLength(8),
        ]),
        newPasswordConfirmation: this.formBuilder.control('', [
          Validators.required,
        ]),
      },
      { validators: this.passwordMatchValidator() }
    );
  }

  ngOnDestroy(): void {
    this.renderer.removeClass(this.document.documentElement, 'page-styles');
    this.renderer.removeClass(this.document.documentElement, 'is-onboarding');
  }

  togglePasswordVisibility(
    field: 'newPassword' | 'newPasswordConfirmation'
  ): void {
    if (field === 'newPassword') {
      this.showNewPassword.set(!this.showNewPassword());
    } else {
      this.showConfirmPassword.set(!this.showConfirmPassword());
    }
  }

  onFormSubmit() {
    if (this.form.valid) {
      const { newPassword, newPasswordConfirmation } = this.form.value;

      this.loading.set(true);

      this.apiService
        .createObj(environment.API.RESET_PASSWORD, {
          token: this.token,
          newPassword,
          newPasswordConfirmation,
        })
        .subscribe({
          next: (response) => {
            this.loading.set(false);
            this.router.navigate([environment.STATES.LOGIN]);
            this.alertService.show({
              type: AlertType.DEFAULT,
              message: this.translateService.instant('recoverPassword.success'),
              duration: 5000,
              inBottom: true,
            });
          },
          error: (error: HttpErrorResponse) => {
            this.loading.set(false);
            this.alertService.show({
              type: AlertType.ERROR,
              message: error.error?.detail ?? `recoverPassword.error`,
              duration: 2000,
              inBottom: true,
            });
          },
        });
    }
  }

  private passwordMatchValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const newPassword = control.get('newPassword')?.value;
      const confirmPassword = control.get('newPasswordConfirmation')?.value;
      return newPassword && confirmPassword && newPassword !== confirmPassword
        ? { mismatch: true }
        : null;
    };
  }
}
